import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  LayoutDashboard, Users, Stethoscope, MessageSquare, Pill, LogOut,
  Settings, Calendar, FileText, ClipboardList, Package, Search,
  UserCircle, Video, Activity, BarChart3, BookOpen, Leaf, ListOrdered, AlertTriangle, Cpu, Building2
} from 'lucide-react';
import { Button } from '../ui/button';

const navItems = {
  admin: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/admin/queue', icon: ListOrdered, label: 'Patient Queue' },
    { to: '/admin/users', icon: Users, label: 'User Management' },
    { to: '/admin/organisations', icon: Building2, label: 'Organisations' },
    { to: '/admin/patients', icon: UserCircle, label: 'Patients' },
    { to: '/admin/pending-reasons', icon: AlertTriangle, label: 'Pending Reasons' },
    { to: '/admin/medicines', icon: Package, label: 'Medicine Inventory' },
    { to: '/admin/medicine-updates', icon: ClipboardList, label: 'Medicine Updates' },
    { to: '/admin/knowledge-base', icon: BookOpen, label: 'Knowledge Base' },
    { to: '/admin/media-library', icon: Video, label: 'Media Library' },
    { to: '/admin/whatsapp', icon: MessageSquare, label: 'WhatsApp' },
    { to: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
    { to: '/admin/system-monitor', icon: Cpu, label: 'System Monitor' },
    { to: '/admin/settings', icon: Settings, label: 'Settings' },
  ],
  doctor: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/admin/queue', icon: ListOrdered, label: 'Patient Queue' },
    { to: '/doctor/patients', icon: UserCircle, label: 'Patients' },
    { to: '/doctor/consultations', icon: Stethoscope, label: 'Consultations' },
    { to: '/doctor/prescriptions', icon: FileText, label: 'Prescriptions' },
    { to: '/doctor/follow-ups', icon: ClipboardList, label: 'Follow-Ups' },
    { to: '/doctor/medicines', icon: Search, label: 'Medicine Search' },
    { to: '/admin/pending-reasons', icon: AlertTriangle, label: 'Pending Reasons' },
    { to: '/admin/medicines', icon: Package, label: 'Medicine Inventory' },
    { to: '/admin/knowledge-base', icon: BookOpen, label: 'Knowledge Base' },
  ],
  counsellor: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/counsellor/patients', icon: UserCircle, label: 'Patients' },
  ],
  pharmacy_manager: [],
  front_desk: [],
};

const roleLabels = {
  admin: 'Administrator',
  doctor: 'Doctor',
  counsellor: 'Counsellor',
};

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const items = navItems[user?.role] || navItems.admin;

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <aside 
      className="w-64 h-screen flex flex-col fixed left-0 top-0"
      style={{ backgroundColor: '#063D2F' }}
      data-testid="sidebar"
    >
      {/* Brand */}
      <div className="p-6 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-[14px] flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Leaf className="w-4 h-4 text-white" strokeWidth={2} />
          </div>
          <div>
            <h1 className="font-heading font-extrabold text-white text-base tracking-[3px] uppercase">
              <span style={{ color: 'rgba(255,255,255,0.5)' }}>{roleLabels[user?.role] || user?.role || 'Eden'}</span> <span style={{ color: 'rgba(255,255,255,0.25)' }}>|</span> EDEN
            </h1>
            <p className="text-[9px] font-bold uppercase tracking-[1.2px]" style={{ color: 'rgba(255,255,255,0.45)' }}>Skin Clinic</p>
          </div>
        </div>
      </div>

      {/* User */}
      <div className="px-4 pb-4">
        <div className="flex items-center gap-3 p-3 rounded-[14px]" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="w-8 h-8 rounded-[10px] flex items-center justify-center text-xs font-bold" style={{ backgroundColor: '#0E5132', color: 'white' }}>
            {getInitials(user?.name)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-white truncate" data-testid="user-name">{user?.name}</p>
            <p className="text-[10px] font-medium" style={{ color: 'rgba(255,255,255,0.45)' }} data-testid="user-role">{roleLabels[user?.role]}</p>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="mx-4 mb-2">
        <div className="h-px" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `sidebar-nav-item ${isActive ? 'active' : ''}`
            }
            style={({ isActive }) => ({
              color: isActive ? 'white' : 'rgba(255,255,255,0.55)',
            })}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <item.icon className="w-[18px] h-[18px]" strokeWidth={1.8} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Sign out */}
      <div className="p-4">
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 rounded-[14px] text-sm"
          style={{ color: 'rgba(255,255,255,0.4)' }}
          onClick={handleLogout}
          data-testid="logout-button"
        >
          <LogOut className="w-[18px] h-[18px]" strokeWidth={1.8} />
          <span>Sign out</span>
        </Button>
      </div>
    </aside>
  );
}
